import { useState, useMemo, useEffect } from 'react';
import { MoreHorizontal, Shield, KeyRound, UserX, UserCheck, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Crown } from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { CanvasserTypeBadge, StatusBadge, PresetBadge, PersonAvatar } from '../shared';
import { ResetPasswordDialog } from '../dialogs';
import { MakesPurchasesControl } from './MakesPurchasesControl';
import { CanvasserType, assertNever } from '@signalvote/shared';
import type { CampaignUserDto } from '@signalvote/shared';
import {
  useUpdateVolunteerRole,
  useToggleUserStatus,
  useDeleteVolunteer,
} from '@/hooks/queries';

type SortField = 'name' | 'preset' | 'status' | 'type' | 'vendor' | 'lastActive';
type SortDirection = 'asc' | 'desc';

interface SortIconProps {
  field: SortField;
  activeField: SortField;
  direction: SortDirection;
}

function SortIcon({ field, activeField, direction }: SortIconProps) {
  if (activeField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground/50" />;
  if (direction === 'asc') return <ArrowUp className="ml-1 h-3 w-3" />;
  return <ArrowDown className="ml-1 h-3 w-3" />;
}

interface PeopleTableProps {
  volunteers: CampaignUserDto[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onEditPermissions?: (volunteer: CampaignUserDto) => void;
  onTransferOwnership?: (volunteer: CampaignUserDto) => void;
  canTransferOwnership?: boolean;
  canManageFinance?: boolean;
  currentUserId?: string;
}

export function PeopleTable({
  volunteers,
  isLoading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onEditPermissions,
  onTransferOwnership,
  canTransferOwnership = false,
  canManageFinance = false,
  currentUserId,
}: PeopleTableProps) {
  const updateRole = useUpdateVolunteerRole();
  const toggleStatus = useToggleUserStatus();
  const deleteVolunteer = useDeleteVolunteer();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<CampaignUserDto | null>(null);

  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const sortedVolunteers = useMemo(() => {
    const sorted = [...volunteers].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
          break;
        case 'preset':
          cmp = ((a.isOwner ? 'Owner' : a.presetKey) || '').localeCompare((b.isOwner ? 'Owner' : b.presetKey) || '');
          break;
        case 'status':
          cmp = (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0);
          break;
        case 'type':
          cmp = (a.canvasserType || '').localeCompare(b.canvasserType || '');
          break;
        case 'vendor':
          cmp = (a.vendorName || '').localeCompare(b.vendorName || '');
          break;
        case 'lastActive': {
          const aTime = new Date(a.lastActivity || a.lastLoginAt || 0).getTime();
          const bTime = new Date(b.lastActivity || b.lastLoginAt || 0).getTime();
          cmp = aTime - bTime;
          break;
        }
        default:
          assertNever(sortField);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [volunteers, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedVolunteers.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedVolunteers = sortedVolunteers.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (volunteers.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No staff members found matching your filters.</p>
      </div>
    );
  }

  const handleRoleChange = (userId: string, newRole: string) => {
    const isPlatformAdmin = newRole.toLowerCase() === 'admin' || newRole.toLowerCase() === 'superadmin';
    updateRole.mutate({ userId, isPlatformAdmin });
  };

  const handleToggleStatus = (userId: string) => {
    toggleStatus.mutate(userId);
  };

  const handleDelete = (userId: string) => {
    if (confirmDeleteId === userId) {
      deleteVolunteer.mutate(userId);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(userId);
      setTimeout(() => setConfirmDeleteId(null), 3000);
    }
  };

  const allSelected = paginatedVolunteers.length > 0 && paginatedVolunteers.every((v) => selectedIds.has(v.userId));
  const someSelected = paginatedVolunteers.some((v) => selectedIds.has(v.userId)) && !allSelected;

  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={onToggleSelectAll}
                />
              </TableHead>
              <TableHead className="w-[280px] text-xs font-semibold uppercase tracking-wider">
                <button className="flex items-center hover:text-foreground" onClick={() => handleSort('name')}>
                  Member <SortIcon field="name" activeField={sortField} direction={sortDirection} />
                </button>
              </TableHead>
              <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wider">
                <button className="flex items-center hover:text-foreground" onClick={() => handleSort('preset')}>
                  Permissions <SortIcon field="preset" activeField={sortField} direction={sortDirection} />
                </button>
              </TableHead>
              <TableHead className="w-[100px] text-xs font-semibold uppercase tracking-wider">
                <button className="flex items-center hover:text-foreground" onClick={() => handleSort('status')}>
                  Status <SortIcon field="status" activeField={sortField} direction={sortDirection} />
                </button>
              </TableHead>
              <TableHead className="w-[100px] text-xs font-semibold uppercase tracking-wider">
                <button className="flex items-center hover:text-foreground" onClick={() => handleSort('type')}>
                  Type <SortIcon field="type" activeField={sortField} direction={sortDirection} />
                </button>
              </TableHead>
              <TableHead className="w-[140px] text-xs font-semibold uppercase tracking-wider">
                <button className="flex items-center hover:text-foreground" onClick={() => handleSort('vendor')}>
                  Vendor <SortIcon field="vendor" activeField={sortField} direction={sortDirection} />
                </button>
              </TableHead>
              <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wider">
                <button className="flex items-center hover:text-foreground" onClick={() => handleSort('lastActive')}>
                  Last Active <SortIcon field="lastActive" activeField={sortField} direction={sortDirection} />
                </button>
              </TableHead>
              <TableHead className="w-[110px] text-xs font-semibold uppercase tracking-wider">
                Makes purchases
              </TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedVolunteers.map((volunteer) => (
              <TableRow
                key={volunteer.userId}
                className={cn(
                  'even:bg-muted/30 hover:bg-accent/50 transition-colors duration-150',
                  selectedIds.has(volunteer.userId) && 'bg-accent/50'
                )}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(volunteer.userId)}
                    onCheckedChange={() => onToggleSelect(volunteer.userId)}
                  />
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-3">
                    <PersonAvatar
                      firstName={volunteer.firstName}
                      lastName={volunteer.lastName}
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">
                        {volunteer.firstName} {volunteer.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {volunteer.email}
                      </p>
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <PresetBadge isOwner={volunteer.isOwner} presetKey={volunteer.presetKey} />
                </TableCell>

                <TableCell>
                  <StatusBadge status={volunteer.isActive ? 'active' : 'inactive'} />
                </TableCell>

                <TableCell>
                  <CanvasserTypeBadge type={volunteer.canvasserType} />
                </TableCell>

                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {volunteer.vendorName || '—'}
                  </span>
                </TableCell>

                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {formatRelativeTime(volunteer.lastActivity || volunteer.lastLoginAt)}
                  </span>
                </TableCell>

                <TableCell>
                  <MakesPurchasesControl
                    volunteer={volunteer}
                    canManageFinance={canManageFinance}
                    isSelf={volunteer.userId === currentUserId}
                  />
                </TableCell>

                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEditPermissions?.(volunteer)}>
                        <Shield className="mr-2 h-4 w-4" />
                        Edit Permissions
                      </DropdownMenuItem>
                      {canTransferOwnership && !volunteer.isOwner && (
                        <DropdownMenuItem onClick={() => onTransferOwnership?.(volunteer)}>
                          <Crown className="mr-2 h-4 w-4" />
                          Transfer Ownership
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => setResetTarget(volunteer)}>
                        <KeyRound className="mr-2 h-4 w-4" />
                        Reset Password
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleToggleStatus(volunteer.userId)}>
                        {volunteer.isActive ? (
                          <>
                            <UserX className="mr-2 h-4 w-4" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <UserCheck className="mr-2 h-4 w-4" />
                            Activate
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDelete(volunteer.userId)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {confirmDeleteId === volunteer.userId ? 'Click again to confirm' : 'Delete'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {sortedVolunteers.length > 10 && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground tabular-nums">
            <span>
              Showing {startIndex + 1}-{Math.min(startIndex + pageSize, sortedVolunteers.length)} of {sortedVolunteers.length}
            </span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground tabular-nums px-2">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ResetPasswordDialog
        volunteer={resetTarget}
        open={resetTarget !== null}
        onOpenChange={(open) => !open && setResetTarget(null)}
      />
    </div>
  );
}
